import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Building2, 
  CreditCard, 
  Save, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  Loader2,
  Phone,
  Mail,
  MapPin,
  Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { TIMEZONES, getTimezonesForCountry, DEFAULT_TIMEZONE_USA } from '../utils/timezones';

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('organization');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Organization data
  const [orgData, setOrgData] = useState({
    name: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    city: '',
    timezone: DEFAULT_TIMEZONE_USA,
    country: 'USA'
  });
  
  // Subscription data
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    fetchProfileData();
    
    // Check if returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId) {
      setActiveTab('billing');
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'canceled') {
      toast.error('Payment was canceled');
      window.history.replaceState({}, '', '/profile');
    }
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      if (orgId) {
        // Get org details from backend
        const orgResponse = await api.get(`/organizations/${orgId}`);
        setOrgData(orgResponse.data);
      }
      
      // Fetch subscription status
      const subResponse = await api.get('/subscriptions/my-status');
      setSubscriptionStatus(subResponse.data);
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganization = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        name: orgData.name,
        ownerName: orgData.ownerName,
        ownerEmail: orgData.ownerEmail,
        ownerPhone: orgData.ownerPhone,
        city: orgData.city,
        timezone: orgData.timezone,
        country: orgData.country
      };
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      await api.put(`/organizations/${orgId}`, updateData);
      
      toast.success('Organization updated successfully!');
      fetchProfileData();
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error('Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setProcessing(false);
      return;
    }

    setProcessing(true);

    try {
      const response = await api.get(`/subscriptions/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Subscription activated! Welcome to BookingFlows!');
        fetchProfileData();
        setProcessing(false);
        window.history.replaceState({}, '', '/profile');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setProcessing(false);
        window.history.replaceState({}, '', '/profile');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please refresh the page.');
      setProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setProcessing(true);
      const originUrl = window.location.origin;
      
      const response = await api.post('/subscriptions/create-checkout', null, {
        params: {
          package_id: 'starter',
          origin_url: originUrl
        }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout. Please try again.');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      return;
    }

    try {
      setProcessing(true);
      await api.post('/subscriptions/cancel');
      toast.success('Subscription canceled');
      fetchProfileData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
      case 'canceled':
        return <Badge className="bg-gray-500 text-white"><XCircle className="w-3 h-3 mr-1" /> Canceled</Badge>;
      case 'past_due':
        return <Badge className="bg-yellow-500 text-white"><AlertCircle className="w-3 h-3 mr-1" /> Past Due</Badge>;
      default:
        return <Badge className="bg-gray-400 text-white"><Clock className="w-3 h-3 mr-1" /> Inactive</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile & Organization</h1>
          <p className="text-gray-600 mt-2">Manage your organization, billing, and account settings</p>
        </div>

        {/* Processing Overlay */}
        {processing && (
          <Card className="border border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Processing payment...</p>
                  <p className="text-sm text-blue-700">Please wait while we verify your subscription</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('organization')}
              className={`pb-4 px-1 border-b font-medium text-sm transition-colors ${
                activeTab === 'organization'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Organization
              </div>
            </button>
            <button
              onClick={() => setActiveTab('owner')}
              className={`pb-4 px-1 border-b font-medium text-sm transition-colors ${
                activeTab === 'owner'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Owner Profile
              </div>
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`pb-4 px-1 border-b font-medium text-sm transition-colors ${
                activeTab === 'billing'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Billing & Subscription
                {subscriptionStatus?.subscriptionStatus === 'active' && (
                  <Badge className="bg-green-500 text-white text-xs">Active</Badge>
                )}
              </div>
            </button>
          </nav>
        </div>

        {/* Organization Tab */}
        {activeTab === 'organization' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Manage your organization's details and settings
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Organization Name */}
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name *</Label>
                  <Input
                    id="orgName"
                    value={orgData.name}
                    onChange={(e) => setOrgData({...orgData, name: e.target.value})}
                    placeholder="e.g., Austin Asset Tours"
                  />
                </div>

                {/* Slug (Read-only) */}
                <div className="space-y-2">
                  <Label htmlFor="slug">Organization Slug</Label>
                  <Input
                    id="slug"
                    value={orgData.slug}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500">Cannot be changed after creation</p>
                </div>

                {/* City */}
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="city"
                      value={orgData.city || ''}
                      onChange={(e) => setOrgData({...orgData, city: e.target.value})}
                      placeholder="e.g., Austin"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Timezone */}
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <select
                    id="timezone"
                    value={orgData.timezone}
                    onChange={(e) => setOrgData({...orgData, timezone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {/* Show country-specific timezones first */}
                    <optgroup label={orgData.country === 'UAE' ? '🇦🇪 UAE Timezones' : '🇺🇸 US Timezones'}>
                      {getTimezonesForCountry(orgData.country).map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="All Timezones">
                      {TIMEZONES.filter(tz => !getTimezonesForCountry(orgData.country).some(ctz => ctz.value === tz.value)).map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  onClick={handleSaveOrganization}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Owner Profile Tab */}
        {activeTab === 'owner' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Owner Profile
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Manage your personal information and account settings
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Owner Name */}
                <div className="space-y-2">
                  <Label htmlFor="ownerName">Full Name *</Label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="ownerName"
                      value={orgData.ownerName}
                      onChange={(e) => setOrgData({...orgData, ownerName: e.target.value})}
                      placeholder="Your full name"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Owner Email */}
                <div className="space-y-2">
                  <Label htmlFor="ownerEmail">Email Address *</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="ownerEmail"
                      type="email"
                      value={orgData.ownerEmail}
                      onChange={(e) => setOrgData({...orgData, ownerEmail: e.target.value})}
                      placeholder="your@email.com"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Owner Phone */}
                <div className="space-y-2">
                  <Label htmlFor="ownerPhone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="ownerPhone"
                      value={orgData.ownerPhone || ''}
                      onChange={(e) => setOrgData({...orgData, ownerPhone: e.target.value})}
                      placeholder="+1-555-0100"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  onClick={handleSaveOrganization}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscription & Billing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Current Status</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-lg font-semibold capitalize">
                      {subscriptionStatus?.subscriptionPlan || 'Free'}
                    </span>
                    {getStatusBadge(subscriptionStatus?.subscriptionStatus)}
                  </div>
                </div>
                {subscriptionStatus?.subscriptionStartDate && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Active Since</p>
                    <p className="text-sm font-medium">
                      {new Date(subscriptionStatus.subscriptionStartDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Subscription Actions */}
              {subscriptionStatus?.subscriptionStatus !== 'active' ? (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-blue-900 text-xl">Starter Plan</h3>
                      <p className="text-blue-700 mt-1">Get started with BookingFlows</p>
                      <div className="mt-3">
                        <span className="text-4xl font-bold text-blue-900">$0.99</span>
                        <span className="text-blue-700 text-lg">/month</span>
                      </div>
                      <ul className="mt-4 space-y-2">
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Unlimited trip scheduling
                        </li>
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Auto-scheduler access
                        </li>
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Widget booking system
                        </li>
                        <li className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          Multi-tenant support
                        </li>
                      </ul>
                    </div>
                  </div>
                  <Button
                    onClick={handleSubscribe}
                    disabled={processing}
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <h3 className="font-bold text-green-900 text-lg">Active Subscription</h3>
                    </div>
                    <p className="text-green-700">You're all set! Enjoying full access to BookingFlows.</p>
                    <p className="text-sm text-green-600 mt-2">
                      Billing: $0.99/month • Renews automatically
                    </p>
                  </div>

                  <Button
                    onClick={handleCancelSubscription}
                    disabled={processing}
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Test Mode Notice */}
              <div className="border-t pt-4">
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Test Mode:</strong> This is a development subscription. 
                    Use test card: 4242 4242 4242 4242, any future date, any CVC.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
